import { useNavigate } from '@tanstack/react-router'
import type { NotificationResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { Skeleton } from '@trellis/roots/skeleton'
import { Typography } from '@trellis/roots/typography'
import { Bell, CheckCircle, Clock } from 'lucide-react'

import {
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useSnoozeNotificationMutation,
} from '@/hooks/useNotifications'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

import { getNavigationPath, getNotificationIcon } from './notification-utils'

interface NotificationListProps {
  onNavigate: () => void
}

function getRelativeTime(dateString: string): string {
  const now = Date.now()
  const then = new Date(dateString).getTime()
  const diffMs = now - then

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`

  return formatDate(dateString)
}

const SNOOZE_OPTIONS = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: 'Tomorrow', hours: 24 },
  { label: 'Next week', hours: 168 },
] as const

export function NotificationList({ onNavigate }: NotificationListProps) {
  const { data: result, isPending } = useNotificationsQuery()
  const markRead = useMarkNotificationReadMutation()
  const snoozeMutation = useSnoozeNotificationMutation()
  const navigate = useNavigate()

  const notifications = result?.data ?? []

  if (isPending) {
    return (
      <div role="status" aria-label="Loading notifications" className="py-1">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="flex items-start gap-3 px-4 py-3">
            <Skeleton variant="circle" className="mt-0.5 size-5 shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton variant="text" className="w-2/3" />
              <Skeleton variant="text" className="w-1/3" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-8">
        <Bell className="text-muted-foreground/50 size-8" aria-hidden="true" />
        <Typography variant="body" color="muted">
          No notifications
        </Typography>
      </div>
    )
  }

  async function handleClick(notification: NotificationResponseDto) {
    if (!notification.isRead) {
      try {
        await markRead.mutateAsync(notification.id)
      } catch {
        /* Navigation still proceeds if mark-read fails */
      }
    }
    const path = getNavigationPath(notification)
    if (path) {
      /* getNavigationPath returns an untyped string, not the route-tree literal union `to`
       * expects — `href` bypasses that check for a value already validated as a real path. */
      void navigate({ href: path })
      onNavigate()
    }
  }

  function handleSnooze(notificationId: string, hours: number) {
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
    snoozeMutation.mutate({ notificationId, until })
  }

  return (
    <ul aria-label="Notifications" className="max-h-[400px] overflow-y-auto">
      {notifications.map((notification) => (
        <li
          key={notification.id}
          className={cn(
            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
            !notification.isRead && 'bg-muted/30',
            notification.resolvedAt && 'opacity-60',
          )}
        >
          <button
            type="button"
            onClick={() => handleClick(notification)}
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
          >
            <div className="mt-0.5">{getNotificationIcon(notification.type)}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p
                  className={cn(
                    'truncate text-sm',
                    !notification.isRead && 'font-medium',
                  )}
                >
                  {notification.title}
                </p>
                {notification.resolvedAt && (
                  <CheckCircle
                    className="size-3.5 shrink-0 text-green-600"
                    aria-label="Resolved"
                  />
                )}
              </div>
              {notification.body && (
                <p className="text-muted-foreground mt-0.5 truncate text-xs">
                  {notification.body}
                </p>
              )}
              <div className="mt-1 flex items-center gap-2">
                <p className="text-muted-foreground/70 text-xs">
                  {getRelativeTime(notification.createdAt)}
                </p>
                {notification.actionLabel && notification.actionUrl && (
                  <span className="text-primary text-xs font-medium">
                    {notification.actionLabel}
                  </span>
                )}
              </div>
            </div>
          </button>
          <div className="flex shrink-0 items-center gap-1">
            {!notification.isRead && (
              <span
                className="mt-2 size-2 shrink-0 rounded-full bg-blue-500"
                role="status"
                aria-label="Unread"
              />
            )}
            {!notification.resolvedAt && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={`Snooze: ${notification.title}`}
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                  >
                    <Clock className="size-3" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {SNOOZE_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.label}
                      onClick={() => handleSnooze(notification.id, option.hours)}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}
